import { createRouter, createWebHistory } from 'vue-router';
import { invalidar as olvidarEditorial } from '@/services/editorialArtists';
import HomeView from '../views/HomeView.vue';
import { BASE_HISTORIAL, IDIOMA, IDIOMAS, IDIOMA_BASE, IDIOMAS_PUBLICOS, enIdioma } from '@/i18n/idioma';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

/**
 * La clave de una pieza que viene en el query string, si es de fiar.
 *
 * 🔴 **De aquí depende que un post de Facebook aterrice en su vídeo.** El
 * servidor publica `astra.fm/butaca?secuencia=<id>` —con el id y no con el
 * slug, porque el slug cambia si se reescribe el título— y Facebook le pega
 * detrás su `fbclid`, que aquí se ignora solo por no mirarlo.
 *
 * ⚠️ **Se valida la forma antes de meterla en una ruta.** Un valor con barras o
 * con `..` construiría una dirección que no es la que se quiere; los ids del
 * contrato son letras, cifras y guiones.
 */
function claveDePieza(valor) {
  const bruto = Array.isArray(valor) ? valor[0] : valor;
  const clave = String(bruto ?? '').trim();
  return /^[A-Za-z0-9_-]{1,120}$/.test(clave) ? clave : '';
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    // Reescrita en C4: va a sangre, con la cabecera y el pie del reset.
    meta: { reset: true },
  },
  // Radar — el buzón. Se llamó «Canal abierto» hasta el 31.08.26. Sin entrada
  // de menú a propósito (decisión de Pablo, 26.08.26): se llega por la puerta de
  // la portada, por el buzón de «Sobre Astra FM» y por el pie.
  // Ver docs/CANAL_ABIERTO.md § 7.
  //
  // ⚠️ Ojo al nombre de los ficheros: los que sirven ESTA sección se siguen
  // llamando `CanalAbiertoView` y `CanalAbiertoDetailView`, y los que se llaman
  // `RadarView` y `RadarDetailView` son **Actualidad**, que se llamó Radar
  // antes. Renombrarlos es tarea aparte; hasta entonces, leer con cuidado.
  {
    path: '/radar',
    name: 'radar',
    component: () => import(/* webpackChunkName: "radar" */ '../views/CanalAbiertoView.vue'),
    meta: { reset: true },
  },
  // La ficha del artista, `Template/Radar · artista 1303:5880`. Va antes que
  // `/radar/:slug` porque si no, «artista» se leería como el slug de un trabajo.
  {
    path: '/radar/artista/:slug',
    name: 'radar-artista',
    component: () => import(/* webpackChunkName: "radar" */ '../views/RadarArtistaView.vue'),
    meta: { reset: true },
  },
  // Donar (06.10.26): pago único en PayPal. `Template/Donar 2398:11365` y
  // `Template/Donar · gracias 2398:28904`. Entrega `docs/entregas/2026-10-06-donar.md`.
  {
    path: '/donar',
    name: 'donar',
    component: () => import(/* webpackChunkName: "donar" */ '../views/DonarView.vue'),
    meta: { reset: true },
  },
  {
    path: '/donar/gracias',
    name: 'donar-gracias',
    component: () => import(/* webpackChunkName: "donar" */ '../views/DonarGraciasView.vue'),
    meta: { reset: true },
  },
  // La página del artista de rotación, `Template/Rotación · artista 2303:11001`
  // (30.09.26). Sustituye al diálogo del 16.09.26. Si la ficha dice que el
  // artista es de Radar, la vista lo manda a `/radar/artista/<slug>`.
  {
    path: '/artista/:slug',
    name: 'artista',
    component: () => import(/* webpackChunkName: "artista" */ '../views/ArtistaView.vue'),
    meta: { reset: true },
  },
  // ⛔ Aquí vivía `/radar/:slug` con `CanalAbiertoDetailView`, la ficha **por
  // trabajo** del diseño anterior. Se retira entera el 01.09.26 por encargo de
  // Pablo: cuando el escaparate pasó a ser por artista, la ficha se mudó a
  // `/radar/artista/<slug>` y esta se quedó sin un solo enlace en la web.
  //
  // 🔴 **Sin redirección.** Se probó a dejarla redirigiendo a la ficha nueva, y
  // es justo lo que no se quiere: un alias que sobrevive a la pieza que lo
  // justificaba. Radar tiene **una** ficha y **una** URL.

  // Los nombres anteriores de la sección, por si alguien los compartió. El
  // buzón ha tenido tres: `/demos`, `/canal-abierto` y ahora `/radar`.
  { path: '/demos', redirect: '/radar' },
  { path: '/canal-abierto', redirect: '/radar' },
  // ⛔ Y con ella se va `/canal-abierto/:slug`, que solo existía para alimentarla:
  // redirigía a una ruta que ya no está. Los dos nombres viejos de la sección
  // —`/demos` y `/canal-abierto`— siguen llevando al escaparate, que sí existe.

  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    // Reescrita en C4: va a sangre, con la cabecera y el pie del reset.
    meta: { reset: true },
  },
  {
    // Galería de desarrollo del Design System (cards Library 26).
    path: '/ds-preview',
    name: 'ds-preview',
    component: () => import(/* webpackChunkName: "ds-preview" */ '../views/DsPreviewView.vue'),
    // `preview` deja fuera el chrome del sitio —cabecera, pie, player, banner de
    // cookies y modal de edad—. Un banco de componentes con el modal encima no
    // sirve para revisar nada, y además monta SiteHeader y SiteFooter como
    // piezas de muestra: con el chrome real saldrían dos veces.
    meta: { title: 'DS Preview - Astra', preview: true },
  },
  // ⚠️ Aquí vivía `/test-app`, la página de apuntarse a la prueba de la app.
  // **Retirada el 24.09.26 con WordPress**: mandaba el alta a
  // `/astraless/wp-json/astra/v1/preview-signup`, un endpoint que se apaga. La
  // casilla de la beta vive ahora en el formulario de cuenta y la recoge el
  // servidor, § `BETA_APP_WEB_INSTRUCCIONES.md`.
  // La puerta: entrar o darse de alta. Con sesión, la propia vista manda al
  // área, así que aquí no hace falta guardia.
  {
    path: '/cuenta',
    name: 'cuenta',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaView.vue'),
    meta: { reset: true },
  },
  // El área. La vista comprueba la sesión y manda a `/cuenta` si no la hay: la
  // guardia va donde está el estado, no en el router, que tendría que importar
  // la store para adivinar lo mismo.
  {
    path: '/cuenta/area',
    name: 'cuenta-area',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaAreaView.vue'),
    meta: { reset: true },
  },
  // El área de artista y de sello. `Template/Artista · mis envíos 1248:5094` y
  // su gemela `Sello · envíos 1253:5748` comparten pantalla: cambia el raíl, no
  // el panel.
  {
    path: '/cuenta/envios',
    name: 'cuenta-envios',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaEnviosView.vue'),
    meta: { reset: true },
  },
  // El envío nuevo cuelga de «mis envíos», igual que proponer cuelga del
  // roster: el raíl sigue marcando el destino del que sale.
  {
    path: '/cuenta/envios/nuevo',
    name: 'cuenta-envio-nuevo',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaEnvioNuevoView.vue'),
    meta: { reset: true },
  },
  {
    path: '/cuenta/ficha',
    name: 'cuenta-ficha',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaFichaView.vue'),
    meta: { reset: true },
  },
  {
    path: '/cuenta/roster',
    name: 'cuenta-roster',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaRosterView.vue'),
    meta: { reset: true },
  },
  // Proponer artista cuelga del roster: es un paso, no un destino. Por eso el
  // raíl del área sigue marcando «Nuestro roster» mientras se está aquí.
  {
    path: '/cuenta/roster/proponer',
    name: 'cuenta-roster-proponer',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaRosterProponerView.vue'),
    meta: { reset: true },
  },
  {
    path: '/cuenta/sello',
    name: 'cuenta-sello',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/CuentaSelloView.vue'),
    meta: { reset: true },
  },
  // 🔴 El perfil NO es una pantalla: es la cuarta sección del área
  // (`08 · ÁREA · OYENTE 1252:5235`). La ruta se queda porque llegó a existir y
  // porque el panel de la cabecera la enlazaba.
  {
    path: '/cuenta/perfil',
    redirect: '/cuenta/area#perfil',
  },
  // Las dos páginas a las que llevan los correos de cuentas. Contrato
  // `CUENTAS_WEB_INSTRUCCIONES.md` §2: las direcciones las fija el servidor, que
  // ya está enviando esos enlaces, así que **no se pueden cambiar** por mucho
  // que en esta casa se prefiera otra cosa.
  {
    path: '/verificar-correo',
    name: 'verificar-correo',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/VerificarCorreoView.vue'),
    meta: { reset: true },
  },
  {
    path: '/restablecer-contrasena',
    name: 'restablecer-contrasena',
    component: () => import(/* webpackChunkName: "cuenta" */ '../views/RestablecerContrasenaView.vue'),
    meta: { reset: true },
  },

  {
    path: '/darse-de-baja',
    name: 'unsubscribe',
    component: () => import(/* webpackChunkName: "unsubscribe" */ '../views/UnsubscribeView.vue'),
    // 🔴 Con la cabecera y el pie del reset desde el 04.11.26 (Pablo): era la
    // última ruta pública con los del diseño anterior. La vista se centra sola.
    meta: { title: 'Darse de baja - Astra FM', reset: true },
  },
  {
    path: '/actualidad',
    name: 'actualidad-archive',
    component: () => import(/* webpackChunkName: "actualidad" */ '../views/RadarView.vue'),
    // Reescrita en C4: va a sangre, con la cabecera y el pie del reset.
    meta: { reset: true },
  },
  {
    path: '/actualidad/:slug',
    name: 'actualidad-detail',
    component: () => import(/* webpackChunkName: "actualidad-detail" */ '../views/RadarDetailView.vue'),
    // Reescrita en C4: va a sangre, con la cabecera y el pie del reset.
    meta: { reset: true },
  },
  // Preview embebido en el manager (iframe + postMessage). Página limpia, sin chrome.
  {
    path: '/preview',
    name: 'radar-preview',
    component: () => import(/* webpackChunkName: "radar-preview" */ '../views/RadarPreviewView.vue'),
    meta: { title: 'Vista previa - Astra', preview: true },
  },
  // ⛔ Aquí estaban los redirects de `/radar` a Actualidad, de cuando esa
  // sección se llamaba Radar. Se retiran el 31.08.26 porque el nombre pasa al
  // buzón, y una ruta no puede llevar a dos sitios.
  //
  // 🔴 **Desde el 18.10.26 lo hace el servidor, con un 301 de nginx:**
  // `/radar/<slug>` de un solo segmento va a `/actualidad/<slug>`, salvo
  // `/radar/artista`. Se pudo cuando `/radar/:slug` del buzón se retiró (01.09),
  // y tiene que ser nginx porque Google y las redes no ven un redirect de JS.
  // Así que esas direcciones **ya no llegan nunca a la web**: una ruta nueva
  // `/radar/<algo>` no funcionaría hasta pedir al servidor que la añada a la
  // excepción de la regla (`nginx-host/astra-web.conf` en su repo).
  // Hilo: `PETICION_` y `RESPUESTA_REDIRECCION_RADAR_A_ACTUALIDAD.md` en temp.
  // 🚫 DESHABILITADO - Flux ya no existe
  // ⚠️ Aquí estaba el canal múltiple —`/canales/:canal` con sus vistas dentro—,
  // comentado desde el reset. **Borrado el 24.09.26**: su layout y sus tres
  // vistas leían de WordPress y se han ido con él. Riot y Flux no existen; solo
  // hay Astra FM.
  // Rutas de Radio (antes Riot)
  {
    // 🔴 **Una sola ruta para las dos vistas de Radio**, desde el 14.09.26:
    // `/radio` es En directo y `/radio/shows` es Shows. Cada vista es una
    // dirección —se comparte por enlace y el atrás vuelve a la anterior—, y al
    // ser el mismo registro la vista no se vuelve a montar al conmutar. Entrega
    // `2026-09-14-shows-dentro-de-radio.md`.
    //
    // Y desde el 19.10.26, **`/radio/lanzamientos`**, la tercera pestaña
    // (entrega `2026-10-19-radio-pestana-lanzamientos.md`).
    path: '/radio/:vista(shows|lanzamientos)?',
    name: 'radio',
    component: () => import(/* webpackChunkName: "radio" */ '../views/RadioView.vue'),
    // El reset va a sangre: sin contenedor centrado y sin ancho máximo.
    // `pestanas`: En directo y Shows son dos pestañas de la misma sección, así
    // que pasar de una a otra no devuelve arriba. Ver `scrollBehavior`.
    meta: { reset: true, pestanas: 'radio' },
  },
  // La página de un disco (19.10.26, entrega `2026-10-19-radio-pagina-del-disco.md`).
  // Dirección decidida por Pablo. Solo tienen página los discos de la lista de
  // Lanzamientos; los demás dan «no está» con `noindex`.
  {
    path: '/lanzamientos/:slug',
    name: 'lanzamiento',
    component: () => import(/* webpackChunkName: "lanzamiento" */ '../views/LanzamientoView.vue'),
    meta: { reset: true },
  },
  // `/lanzamientos` a secas es la pestaña: la página del disco dice «Volver a
  // Lanzamientos», y quitarle el slug a su dirección tiene que llevar ahí.
  {
    path: '/lanzamientos',
    redirect: '/radio/lanzamientos'
  },
  // Shows fue página propia hasta el 14.09.26: ahora es la vista Shows de
  // Radio. La redirección cubre enlaces viejos y los de redes. Solo `/shows` a
  // secas: `/shows/:id` es la ficha del programa y se queda.
  {
    path: '/shows',
    redirect: '/radio/shows'
  },
  {
    path: '/shows/:id',
    name: 'show-detail',
    component: () => import(/* webpackChunkName: "show-detail" */ '../views/ShowDetailView.vue'),
    // Reescrita en C4: va a sangre, con la cabecera y el pie del reset. El
    // título lo pone la vista con `useHead`, junto al resto de su SEO.
    meta: { reset: true }
  },
  // ── Butaca ──
  //
  // 🔴 **TV pasa a llamarse Butaca el 21.09.26** y se divide en dos salas:
  // videoclips —lo que era TV, de `tv.json`— y secuencias —fragmentos de cine,
  // de `secuencias.json`—. Entrega `docs/entregas/2026-09-21-butaca-y-pestanas.md`.
  {
    path: '/butaca',
    name: 'butaca',
    component: () => import(/* webpackChunkName: "butaca" */ '../views/ButacaView.vue'),

    // 🔴 **`?secuencia=<id>` y `?video=<id>` llevan a la pieza**, no a la home
    // de la sección. Es el § 4.1 del contrato de secuencias, y **es la
    // dirección con la que el servidor publica en Facebook y en Bluesky**.
    //
    // ⚠️ **Esto faltaba y yo di por hecho que estaba.** Escribí al servidor que
    // la ruta funcionaba sin comprobar el aterrizaje: el `butaca-og.php`
    // resolvía bien la tarjeta —eso sí estaba probado—, pero **esa equivalencia
    // vivía solo en el PHP**, y la aplicación no miraba el parámetro. Lo cazó el
    // servidor el 21.09.26 con el post de Fight Club ya publicado, que no se
    // puede cambiar. De ahí que se arregle aquí y no cambiando lo que publican.
    //
    // Va con `replace` para que el atrás del navegador no devuelva a esta
    // dirección y vuelva a rebotar.
    beforeEnter: (to) => {
      const secuencia = claveDePieza(to.query.secuencia);
      if (secuencia) return { path: `/butaca/secuencia/${secuencia}`, replace: true };

      const video = claveDePieza(to.query.video);
      if (video) return { path: `/butaca/videoclip/${video}`, replace: true };

      return true;
    },

    // `pestanas` agrupa las rutas que son dos pestañas de la misma sección: al
    // pasar de una a otra no se vuelve arriba. Ver `scrollBehavior`.
    meta: { reset: true, pestanas: 'butaca' }
  },
  {
    // La otra sala. Va por ruta y no por parámetro para que se pueda enlazar y
    // compartir: son dos páginas, aunque compartan plantilla.
    path: '/butaca/secuencias',
    name: 'butaca-secuencias',
    component: () => import(/* webpackChunkName: "butaca" */ '../views/ButacaView.vue'),
    meta: { reset: true, pestanas: 'butaca' }
  },
  {
    // 🔴 **Cada pieza tiene dirección propia**, que es lo único que pedía el
    // diseño sobre las URL: sin ella no se puede compartir un vídeo.
    //
    // ⚠️ Acepta **slug o id**. El slug es lo que se lee y se indexa; el id es lo
    // que publica el servidor en redes y lo único que NO cambia si la redacción
    // reescribe el título. Mismo criterio que tenía `/tv/:video`.
    path: '/butaca/videoclip/:pieza',
    name: 'butaca-videoclip',
    component: () => import(/* webpackChunkName: "butaca" */ '../views/ButacaDetalleView.vue'),
    meta: { reset: true }
  },
  {
    path: '/butaca/secuencia/:pieza',
    name: 'butaca-secuencia',
    component: () => import(/* webpackChunkName: "butaca" */ '../views/ButacaDetalleView.vue'),
    meta: { reset: true }
  },

  // ── Lo que era TV ──
  //
  // 🔴 **Se redirige, no se borra.** Hay enlaces publicados en Facebook y en
  // Bluesky apuntando a `/tv` y a `/tv/<slug>` desde antes del cambio de nombre,
  // y el `tv-og.php` del servidor los sigue citando. Una sección renombrada que
  // devuelve 404 rompe cada post que se publicó.
  {
    path: '/tv',
    // `tv-og.php` aceptaba `?video=<id>` desde agosto, así que hay posts
    // publicados con esa forma: se les respeta la pieza en vez de dejarlos en
    // la portada de la sección.
    // ⚠️ `query: {}` a propósito: una redirección arrastra el query string por
    // defecto y la dirección acababa en `/butaca/videoclip/<id>?video=<id>`,
    // con el parámetro repetido y ya sin uso.
    redirect: (to) => {
      const video = claveDePieza(to.query.video);
      return video
        ? { path: `/butaca/videoclip/${video}`, query: {} }
        : { path: '/butaca', query: {} };
    }
  },
  {
    path: '/tv/:video',
    redirect: (to) => `/butaca/videoclip/${to.params.video}`
  },
  // Redirects para compatibilidad
  {
    path: '/canales/riot',
    redirect: '/radio'
  },
  {
    path: '/riot',
    redirect: '/radio'
  },
  {
    path: '/riot/shows',
    redirect: '/radio/shows'
  },
  {
    path: '/riot/tv',
    redirect: '/tv'
  },
  {
    path: '/radio/tv',
    redirect: '/tv'
  },
  // 🚫 DESHABILITADO - Flux ya no existe
  // ⚠️ Aquí estaba `/flux`, comentada desde que el canal se retiró. **Borrada
  // el 24.09.26**: su vista leía de WordPress —era la última que lo hacía— y se
  // fue con él.
  // ══════════════════════════════════════════════════════════════
  // Lo que comía de WordPress · retirado el 24.09.26
  // ══════════════════════════════════════════════════════════════
  //
  // 🔴 **WordPress se apaga.** Ya no alimenta nada de la web: el contenido lo
  // sirve `listen.astra.fm` desde el 4-sep. Medido el 24.09.26 navegando las
  // ocho secciones: **la única que seguía llamándolo era `/musica`**, con
  // cuatro peticiones a `/astraless/wp-json`. Las demás, cero.
  //
  // Estas dos vistas leían de ahí y **ya no tenían de dónde leer**: se quedaban
  // vacías o a medias. Se retiran con redirección y no se borran a secas porque
  // **no estaban en el menú**: a ellas se llega por un enlace viejo, y quien lo
  // siga merece acabar en algún sitio.
  //
  //   /musica        →  /radio     la música de la casa está ahí
  //   /artist/:name  →  /radar     donde viven las fichas de artista que hay
  //
  // ⚠️ El nombre del artista se pierde en el camino. Cuando se escribió esto el
  // artista de rotación no tenía página; desde el 30.09.26 la tiene, en
  // `/artista/<slug>`, pero estas direcciones son de WordPress y su `:name` no
  // es garantía de ser el slug del servidor.
  {
    path: '/musica',
    redirect: '/radio',
  },
  {
    path: '/artist/:name',
    redirect: '/radar',
  },
  // Rutas legales
  {
    path: '/legal/terms',
    name: 'terms',
    component: () => import(/* webpackChunkName: "legal-terms" */ '../views/legal/TermsView.vue'),
    meta: { title: 'Términos y Condiciones - Astra FM', reset: true }
  },
  {
    path: '/legal/privacy',
    name: 'privacy',
    component: () => import(/* webpackChunkName: "legal-privacy" */ '../views/legal/PrivacyView.vue'),
    meta: { title: 'Política de Privacidad - Astra FM', reset: true }
  },
  {
    path: '/legal/cookies',
    name: 'cookies',
    component: () => import(/* webpackChunkName: "legal-cookies" */ '../views/legal/CookiesView.vue'),
    meta: { title: 'Política de Cookies - Astra FM', reset: true }
  },
  {
    path: '/legal/contact',
    name: 'contact',
    component: () => import(/* webpackChunkName: "legal-contact" */ '../views/legal/ContactView.vue'),
    // Reescrita el 18.09.26: va a sangre, con la cabecera y el pie del reset.
    meta: { title: 'Contacto - Astra FM', reset: true }
  },
  // 🔴 **La última, y tiene que ser la última**: recoge lo que no ha casado con
  // ninguna ruta de arriba. Sin ella, una URL desconocida no montaba ninguna
  // vista y dejaba la página en blanco entre la cabecera y el pie.
  //
  // Es la pieza que permite **retirar cosas sin dejar una redirección detrás**:
  // hasta hoy, quitar una ruta obligaba a elegir entre un alias que sobrevive a
  // aquello que lo justificaba y un hueco mudo.
  //
  // ⚠️ El `.htaccess` sirve `index.html` para todo, así que el servidor responde
  // **200** aunque aquí se diga que no existe. Por eso la vista se marca
  // `noindex`: es lo único que impide que un buscador guarde cada dirección rota
  // como una página más del sitio.
  {
    path: '/:sinRuta(.*)*',
    name: 'no-existe',
    component: () => import(/* webpackChunkName: "no-existe" */ '../views/NoExisteView.vue'),
    meta: { reset: true },
  },
];

// Dónde estaba la página justo antes de navegar.
//
// 🔴 Hace falta porque `scrollBehavior` se llama **cuando la navegación ya está
// hecha**, y para entonces `window.scrollY` puede ser 0: el sitio del que
// veníamos ya no existe. Solo lo usa el caso de las pestañas, que es el único
// que quiere volver a donde estabas.
let scrollAntesDeNavegar = 0;

// ── El anclaje del navegador, apagado mientras se cambia de pestaña ──
//
// 🔴 **Es la mitad del salto que vio Pablo el 21.09.26.** Chrome tiene una
// función, el *scroll anchoring*, que al cambiar el contenido mueve el
// desplazamiento para que lo que estabas mirando no se te escape. En una página
// que carga imágenes es justo lo que quieres; **al cambiar de pestaña no**,
// porque lo que cambia es el panel entero y el navegador te ancla a algo que ya
// no existe. Medido: de Secuencias a Videoclips, el navegador saltaba solo de
// 477 a 782, y después nuestra colocación lo devolvía a 477. Dos movimientos
// donde no debería haber ninguno.
//
// Se apaga **solo durante el cambio** y se devuelve al soltar, para no perderlo
// en el resto del sitio.
function anclaje(valor) {
  if (typeof document !== "undefined") document.body.style.overflowAnchor = valor;
}

/**
 * Dónde tiene que quedar la vista después de cambiar de pestaña.
 *
 * 🔴 **Lo que tiene que quedarse quieto son las pestañas**, no el número de
 * píxeles: es lo que la persona está mirando y acaba de pulsar. Si estabas por
 * debajo de ellas se vuelve a ellas, justo bajo la cabecera; si estabas por
 * encima, mirando el hero, se conserva el sitio. Y si el panel nuevo es más
 * corto —Secuencias tiene 5 piezas y Videoclips 44— manda el final de la
 * página, que más abajo no se puede ir.
 */
function sitioDeLasPestanas(y) {
  const pestanas = document.querySelector(".pestanas");
  const suSitio = pestanas
    ? pestanas.getBoundingClientRect().top + window.scrollY - (document.querySelector(".site-header")?.offsetHeight || 46)
    : Infinity;
  const tope = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  return Math.max(0, Math.min(y, suSitio, tope));
}

// La vigilancia en curso, para no dejar dos a la vez si se pulsan dos pestañas
// seguidas.
let soltarLaVista = null;

/**
 * Mantiene la vista en su sitio mientras el panel nuevo termina de llegar.
 *
 * 🔴 **Hace falta porque el panel entra vacío.** Su contenido llega por red y
 * la altura crece después: medido el 21.09.26, en Butaca a los 14 ms y **en
 * Radio a los 91**. La versión anterior esperaba a que la altura se asentara y
 * colocaba entonces, y eso era el comportamiento raro que vio Pablo: el panel
 * cambiaba al instante y **la página se movía sola un cuarto de segundo
 * después**, ya quieta, deslizándose.
 *
 * Aquí se coloca en cuanto hay algo que colocar, y otra vez cada vez que el
 * panel cambia de alto. Como cada colocación es instantánea y va al mismo
 * sitio, repetirla no se ve.
 *
 * ⚠️ **Quien toca la rueda manda**: al primer gesto se suelta y no se le vuelve
 * a mover la página a nadie.
 */
function mantenLaVista(y) {
  if (soltarLaVista) soltarLaVista();

  const app = typeof document !== "undefined" ? document.getElementById("app") : null;
  if (!app || typeof ResizeObserver === "undefined") {
    anclaje("");
    return;
  }

  const observador = new ResizeObserver(() => {
    window.scrollTo({ top: sitioDeLasPestanas(y), behavior: "instant" });
  });

  const suelta = () => {
    soltarLaVista = null;
    observador.disconnect();
    anclaje("");
    window.removeEventListener("wheel", suelta);
    window.removeEventListener("touchstart", suelta);
    window.removeEventListener("keydown", suelta);
  };

  soltarLaVista = suelta;
  observador.observe(app);
  window.addEventListener("wheel", suelta, { once: true, passive: true });
  window.addEventListener("touchstart", suelta, { once: true, passive: true });
  window.addEventListener("keydown", suelta, { once: true });

  // Tope: pasado medio segundo el panel ya está, y lo que venga después
  // —una imagen tardona— no justifica moverle la página a nadie.
  setTimeout(suelta, 500);
}

const router = createRouter({
  // 🔴 **La base es el prefijo del idioma**: `/` en español, `/en/` en inglés.
  // Las rutas de abajo no cambian y los enlaces salen ya con su prefijo. Ver
  // `src/i18n/idioma.js`.
  history: createWebHistory(BASE_HISTORIAL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (por ejemplo, al usar back/forward), la usa
    if (savedPosition) {
      return savedPosition;
    }

    // 🔴 Cambiar SOLO la query —un filtro, una pestaña— no es ir a otra
    // página, y devolver arriba tira al lector fuera de donde estaba mirando.
    // Pasaba en el archivo de Actualidad: pulsar un filtro daba un salto al
    // principio de la página.
    //
    // ⚠️ **Salvo que haya ancla.** Un ancla dentro de la misma página SÍ quiere
    // ir a un sitio, y esta condición se la comía: desde `/cuenta/area`, la fila
    // «pedir perfil» del panel apuntaba a `/cuenta/area#perfil` y no pasaba nada
    // —el clic no llevaba a ningún sitio—. Lo cazó el servidor mirando una
    // cuenta real el 01.09.26.
    if (to.path === from.path && !to.hash) {
      return false;
    }

    // 🔴 **Cambiar de pestaña tampoco es ir a otra página.** Lo vio Pablo el
    // 21.09.26: en Butaca las pestañas van debajo del hero, así que al pulsarlas
    // la página volvía arriba y **las pestañas se te iban de la vista**, que es
    // justo donde estabas mirando. Pasa igual en Radio.
    //
    // Las dos rutas de una misma sección comparten `meta.pestanas`, y entre
    // ellas se conserva el sitio. Con ancla no: un ancla sí quiere ir a un
    // punto.
    if (to.meta?.pestanas && to.meta.pestanas === from.meta?.pestanas && !to.hash) {
      const y = scrollAntesDeNavegar;
      if (!y) {
        anclaje("");
        return false;
      }

      // ⚠️ **`behavior: "instant"`, y no `"auto"`.** `auto` no quiere decir
      // «al instante»: quiere decir «lo que diga el CSS», y el CSS lo dice
      // Bootstrap, que sigue cargado del diseño anterior y trae en su reboot un
      // `@media (prefers-reduced-motion: no-preference) { :root { scroll-behavior: smooth } }`.
      // Con `auto` la colocación **se animaba**: en Radio, 700 px deslizándose
      // durante 430 ms con la página ya quieta. Medido el 21.09.26. Un cambio de
      // pestaña no viaja, se planta.
      mantenLaVista(y);
      return { top: sitioDeLasPestanas(y), behavior: "instant" };
    }

    // Un enlace con ancla quiere ESE punto de la página, no el principio.
    // Lo pide el pie, que manda al formulario de Canal abierto y no a su
    // convocatoria. El `top` deja libre la cabecera, que es fija y taparía el
    // título de la sección.
    //
    // 🔴 Hay que ESPERAR, y no basta con un retardo fijo.
    //
    // El contenido de estas páginas llega por red y cambia la altura después
    // de navegar: saltando de inmediato se aterriza donde estaba el ancla hace
    // un momento —medido en Canal abierto: el formulario quedaba 471px por
    // debajo del sitio al que se había saltado—. Se espera a que la altura del
    // documento deje de moverse, con tope de dos segundos para no dejar la
    // navegación colgada si algo no termina de cargar nunca.
    if (to.hash) {
      return new Promise((resolve) => {
        const destino = { el: to.hash, top: 60, behavior: 'smooth' };
        const limite = Date.now() + 2000;
        let anterior = -1;

        const cuandoSeAsiente = () => {
          const altura = document.documentElement.scrollHeight;
          const quieta = altura === anterior;
          anterior = altura;
          if (quieta || Date.now() > limite) {
            resolve(document.querySelector(to.hash) ? destino : { top: 0 });
            return;
          }
          setTimeout(cuandoSeAsiente, 120);
        };

        setTimeout(cuandoSeAsiente, 120);
      });
    }

    // Navegación de verdad: al principio.
    return { top: 0, behavior: 'smooth' };
  },
});

// Se apunta el sitio antes de cada navegación, para el caso de las pestañas.
router.beforeEach((to, from, next) => {
  scrollAntesDeNavegar = typeof window !== "undefined" ? window.scrollY : 0;

  const cambioDePestana =
    to.meta?.pestanas && to.meta.pestanas === from.meta?.pestanas && !to.hash;
  anclaje(cambioDePestana ? "none" : "");

  next();
});

// El área de cuenta pide sesión, y se comprueba aquí y no solo en cada vista.
//
// ⚠️ Cada pantalla del área ya redirige si no hay sesión, pero **solo al
// montarse**: quien cerraba sesión estando dentro se quedaba viendo sus datos
// hasta recargar, y una URL de área pegada en la barra entraba a pintar el
// esqueleto antes de rebotar. El guard corta las dos por lo sano.
//
// `/cuenta` a secas queda fuera a propósito: es la puerta —crear cuenta y
// entrar— y ahí no haber sesión es lo normal.
router.beforeEach((to) => {
  if (!to.path.startsWith('/cuenta/')) return true;
  const hayToken = (() => {
    try {
      return !!localStorage.getItem('astra_cuenta_token');
    } catch (e) {
      // Navegador con el almacenamiento capado: que decida la vista, que sabrá
      // pedir `/me` y rebotar con un 401.
      return true;
    }
  })();
  // 🔴 **Y se recuerda a dónde iba.** `CuentaView` sabe volver —lee `?volver=`
  // al entrar— pero nadie se lo pasaba, así que una dirección del área pegada en
  // la barra sin sesión te dejaba en la puerta y, tras entrar, en «mi cuenta»,
  // lejos de lo que habías pedido.
  //
  // Importa fuera de la web: la app móvil sale al navegador con enlaces del área
  // —«manda tu música», «gestiona tu ficha»— y allí no hay sesión, porque el
  // token del móvil es suyo y va por cabecera. Sin esto, cada salto del móvil
  // aterriza en la puerta y pierde el destino.
  return hayToken ? true : { path: '/cuenta', query: { volver: to.fullPath } };
});

// La redacción edita fichas en caliente y espera verlas al cambiar de sección,
// sin recargar la página entera: §4 de ARTISTAS_WEB_INSTRUCCIONES, 05.09.26. La
// navegación es el momento honesto para preguntar, igual que volver a la pestaña
// lo es en la portada.
//
// Solo se olvida la capa editorial, no todo el contenido: `olvidarContenido()`
// tira además actualidad, programas, TV y Radar, y dispararlos en cada
// navegación sería pedir por pedir. Aquí se tira una memoria de vista, y lo que
// cuesta rehacerla es un 304 en 0 bytes mientras la redacción no toque nada.
router.afterEach(() => {
  olvidarEditorial();
});

// Añadir un hook global después de cada cambio de ruta
router.afterEach((to) => {
  const canonicalLink = document.querySelector("link[rel='canonical']");
  // 🔴 **El dominio canónico vuelve a ser `astra.fm` el 12.09.26.** Estuvo un
  // día en `dynamo.fm`; la decisión de marca se revisó y se deshizo. Es el
  // dominio que sirve el sitio, así que es el que se señala como original.
  const baseUrl = 'https://astra.fm';

  // Cada idioma es su propia canónica: `/en/radar` no es copia de `/radar`.
  const ruta = enIdioma(to.path, IDIOMA);

  if (canonicalLink) {
    // Si ya existe, actualiza el valor href
    canonicalLink.setAttribute('href', `${baseUrl}${ruta}`);
  } else {
    // Si no existe, crea la etiqueta canónica
    const newCanonicalLink = document.createElement('link');
    newCanonicalLink.setAttribute('rel', 'canonical');
    newCanonicalLink.setAttribute('href', `${baseUrl}${ruta}`);
    document.head.appendChild(newCanonicalLink);
  }

  // 🔴 **Las otras versiones de esta página, para los buscadores** (`hreflang`),
  // y `x-default` en la española. Solo con los idiomas publicados: señalar
  // páginas con `noindex` no sirve de nada. Se reescriben en cada navegación.
  document.head.querySelectorAll("link[rel='alternate'][hreflang]").forEach((l) => l.remove());
  if (IDIOMAS_PUBLICOS && !to.meta?.preview) {
    for (const [hreflang, idioma] of [...IDIOMAS.map((i) => [i, i]), ['x-default', IDIOMA_BASE]]) {
      const alt = document.createElement('link');
      alt.setAttribute('rel', 'alternate');
      alt.setAttribute('hreflang', hreflang);
      alt.setAttribute('href', `${baseUrl}${enIdioma(to.path, idioma)}`);
      document.head.appendChild(alt);
    }
  }
});

// ❌ DESACTIVADO: Spinner global de navegación
// Las páginas ahora cargan instantáneamente con spinners por componente
// router.beforeEach((to, from, next) => {
//   isLoading.value = true;
//   next();
// });

// router.afterEach(() => {
//   isLoading.value = false;
// });

// ── Recargar cuando falta un trozo del build ──
//
// 🔴 **Es el fallo de «hago clic y no pasa nada».** Pablo, 24.09.26: «cuando la
// página está inactiva un rato la navegación falla».
//
// Cada vista se carga aparte, en su propio fichero, y **cada despliegue los
// renombra con una firma nueva**. Quien tiene la web abierta desde antes sigue
// pidiendo los nombres viejos, que ya no existen: la ruta no llega a montarse y
// el clic se queda sin efecto, sin decir nada a quien está delante. En la
// consola se veía `ChunkLoadError` y un `Unexpected token '<'`, porque el
// servidor devolvía la página en vez del fichero —eso ya está arreglado en el
// `.htaccess`, que ahora responde 404—.
//
// La salida es recargar en la dirección a la que se iba: el navegador pide el
// `index.html` nuevo, con los nombres nuevos, y la persona acaba donde quería.
// Se pierde el estado de la página, que es el precio, pero la alternativa es un
// botón que no hace nada.
//
// ⚠️ **Con seguro contra el bucle.** Si la recarga volviera a fallar por lo
// mismo —un despliegue a medias, un proxy que devuelve basura— esto recargaría
// sin parar. Así que solo se intenta **una vez por dirección**, anotado en la
// sesión del navegador: si vuelve a ocurrir, se deja el error a la vista en vez
// de dejar la pestaña dando vueltas.
const MARCA_RECARGA = "astra:recarga-por-trozo";

function faltaUnTrozo(error) {
  const texto = `${error?.name || ""} ${error?.message || ""}`;
  return /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    texto,
  );
}

router.onError((error, to) => {
  if (typeof window === "undefined" || !faltaUnTrozo(error)) return;

  // `fullPath` va sin el prefijo del idioma: `createHref` se lo pone, o la
  // recarga devolvería a la persona al español.
  const destino = to?.fullPath ? router.options.history.createHref(to.fullPath) : window.location.pathname;
  try {
    if (sessionStorage.getItem(MARCA_RECARGA) === destino) return;
    sessionStorage.setItem(MARCA_RECARGA, destino);
  } catch (e) {
    // Sin almacenamiento no hay seguro, pero tampoco hay bucle que valga la
    // pena evitar a costa de no recuperar a nadie: se recarga igual.
  }

  window.location.assign(destino);
});

// Una navegación que sale bien borra el seguro: el siguiente despliegue vuelve
// a tener su oportunidad.
router.afterEach(() => {
  try {
    sessionStorage.removeItem(MARCA_RECARGA);
  } catch (e) {
    // Da igual: el seguro solo evita un bucle, no hace falta limpiarlo.
  }
});

export default router;



